import { errorReporter } from "./lib/errorReporter";

// Initialize error monitoring before anything else can throw.
errorReporter.init();

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { RequireAuth } from "./components/RequireAuth";
import { getAddress, logout } from "./lib/auth";
import { loadChains } from "./lib/chainSelect";
import { loadRuntimeConfig } from "./lib/runtimeConfig";
import { applyTheme, initialTheme } from "./lib/theme";
import { getSelectedRdns, restoreWalletSession, WALLETCONNECT_RDNS } from "./lib/wallet";
import { FaucetPage } from "./pages/FaucetPage";
import { HistoryPage } from "./pages/HistoryPage";
import { LoginPage } from "./pages/LoginPage";
import { OrdersPage } from "./pages/OrdersPage";
import { TradePage } from "./pages/TradePage";
import "./styles.css";

// Before the first frame: a light flash before a dark app is a visible bug.
applyTheme(initialTheme());

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container #root not found");
}

// Deep links fall back to index.html in the vite dev server and nginx.
const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        path: "/",
        element: <App />,
        children: [
          { index: true, element: <TradePage /> },
          { path: "orders", element: <OrdersPage /> },
          { path: "faucet", element: <FaucetPage /> },
          { path: "history", element: <HistoryPage /> },
        ],
      },
    ],
  },
]);

const root = createRoot(container);

// The router mounts only once the descriptor has settled: addresses belong to the
// deployment, and the trade page must never sign against a stale or absent LOP.
// A browser pinned to WalletConnect waits on an import and a relay handshake first.
const reconnecting = getSelectedRdns() === WALLETCONNECT_RDNS;

root.render(
  <StrictMode>
    <div className="boot-screen" role="status" aria-label="Loading">
      <span className="spinner" />
      {reconnecting && <p className="boot-note">Reconnecting your wallet…</p>}
    </div>
  </StrictMode>,
);

// The chain decides which gateway serves /config, and /config names the
// WalletConnect project id that a restored session needs.
void loadChains()
  .then(() => loadRuntimeConfig())
  // With the stored address, a pairing that moved to another account while the
  // tab was closed reads as stale: nothing reports that change.
  .then(() => restoreWalletSession(getAddress()))
  .then((restore) => {
    // "stale": the gateway would still answer /auth/me for a session that can no
    // longer sign, so drop it. "pending" is a slow handshake: mount and adopt later.
    if (restore === "stale") logout();
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </StrictMode>,
    );
  });
